import {
  Creation,
  CreationSet,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatCreativeType,
  CreativeFormatPlaybackTime,
  DisplaySize,
  Maybe,
  Option,
  PricingTable,
  PricingTableCell,
  ProductOption,
  ProductOptionPoint,
  ProductPricing,
} from '../generated/graphql';
import { InputtedCreationSet } from '../view/types/InputtedCreationSet';
import { CreativeFormatItemType, ProductPricingKey } from '../view/types/Pricing';

const calculateWithOptionPoints = (
  key: string,
  creativeFormat: {
    creativeType: {
      requiredPoints: Array<Pick<ProductPricing, 'key' | 'point'>>;
    };
    attribute: {
      aspectRatio?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
      playbackTime?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
    };
  },
) => {
  const requiredPoint = creativeFormat.creativeType.requiredPoints.find((requiredPoint) => requiredPoint.key === key);
  return requiredPoint!.point + calculateOptionPoints(key, creativeFormat);
};

export const calculateOptionPoints = (
  key: string,
  creativeFormat: {
    attribute: {
      aspectRatio?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
      playbackTime?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
    };
  },
) => {
  const optionAspectRatioPoint = creativeFormat.attribute.aspectRatio
    ? creativeFormat.attribute.aspectRatio.requiredOption
      ? creativeFormat.attribute.aspectRatio.requiredOption.points.find((ro_point) => ro_point.key === key)
        ? creativeFormat.attribute.aspectRatio.requiredOption.points.find((ro_point) => ro_point.key === key)!.point *
          creativeFormat.attribute.aspectRatio.requiredOption.amount
        : 0
      : 0
    : 0;

  const optionPlaybackTimePoint = creativeFormat.attribute.playbackTime
    ? creativeFormat.attribute.playbackTime.requiredOption
      ? creativeFormat.attribute.playbackTime.requiredOption.points.find((ro_point) => ro_point.key === key)
        ? creativeFormat.attribute.playbackTime.requiredOption.points.find((ro_point) => ro_point.key === key)!.point *
          creativeFormat.attribute.playbackTime.requiredOption.amount
        : 0
      : 0
    : 0;

  return optionAspectRatioPoint + optionPlaybackTimePoint;
};

export function calculateModificationRequestPoint(
  creation: Pick<Creation, 'modificationRequestPoint'> & {
    creativeFormat: Pick<CreativeFormat, 'resizeBaseId'> & {
      attribute: {
        aspectRatio?: Maybe<{
          requiredOption?: Maybe<
            Pick<ProductOption, 'amount'> & {
              points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
            }
          >;
        }>;
        playbackTime?: Maybe<{
          requiredOption?: Maybe<
            Pick<ProductOption, 'amount'> & {
              points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
            }
          >;
        }>;
      };
    };
  },
) {
  const creativeFormat = creation.creativeFormat;
  if (creativeFormat.resizeBaseId) {
    const requiredPoint = creation.modificationRequestPoint ? creation.modificationRequestPoint : 0;
    const requiredOptionPoint = calculateOptionPoints('modificationRequestForResize', creativeFormat);

    return requiredPoint + requiredOptionPoint;
  } else {
    const requiredPoint = creation.modificationRequestPoint ? creation.modificationRequestPoint : 0;
    const requiredOptionPoint = calculateOptionPoints('modificationRequestForCreate', creativeFormat);

    return requiredPoint + requiredOptionPoint;
  }
}

export const calculateExpressPerDay = (
  creativeFormat: Pick<CreativeFormat, 'resizeBaseId'> & {
    creativeType: {
      requiredPoints: Array<Pick<ProductPricing, 'key' | 'point'>>;
    };
    attribute: {
      aspectRatio?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
      playbackTime?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
    };
  },
  expressDueDays?: number | null,
) => {
  if (creativeFormat.resizeBaseId) {
    const requiredWithOptionPoint = calculateWithOptionPoints('expressPerDayForResize', creativeFormat);
    return requiredWithOptionPoint * (expressDueDays || 0);
  } else {
    const requiredWithOptionPoint = calculateWithOptionPoints('expressPerDayForCreate', creativeFormat);
    return requiredWithOptionPoint * (expressDueDays || 0);
  }
};

// CreativeFormatのOriginal/Resizeで消費金額を計算
export const calculatePoint = (
  creativeFormat: Pick<CreativeFormat, 'resizeBaseId'> & {
    creativeType: {
      requiredPoints: Array<Pick<ProductPricing, 'key' | 'point'>>;
    };
    attribute: {
      aspectRatio?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
      playbackTime?: Maybe<{
        requiredOption?: Maybe<
          Pick<ProductOption, 'amount'> & {
            points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
          }
        >;
      }>;
    };
  },
) => {
  return calculateWithOptionPoints(creativeFormat.resizeBaseId ? 'resize' : 'create', creativeFormat);
};

// 選択されたOptionの合計金額
export const calculateSelectedOptionTotalPoint = (
  creationSet: InputtedCreationSet,
  availablePricingMerchandiseOptions: ReadonlyArray<{
    id: string;
    pricingTables: Array<
      Pick<PricingTable, 'creativeTypeId'> & {
        cells: Array<
          Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] }
        >;
      }
    >;
  }>,
  selectedPricingMerchandiseOptions: ReadonlyArray<string>,
) => {
  const filteredPricingMerchandiseOptions = availablePricingMerchandiseOptions.filter((productOption) =>
    selectedPricingMerchandiseOptions.includes(productOption.id),
  );

  return creationSet.creativeFormats.reduce((accum, inputtedCreativeFormat) => {
    const creativeFormat = {
      ...inputtedCreativeFormat,
      selectedPricingMerchandiseOptions: filteredPricingMerchandiseOptions,
    };

    const point = calculateOptionMerchandisePoint(creativeFormat, creationSet.expressDueDays || 0);
    return accum + point;
  }, 0);
};

export const calculateOptionMerchandisePoint = (
  creativeFormat: Pick<CreativeFormat, 'resizeBaseId'> & {
    creativeType: Pick<CreativeFormatCreativeType, 'key' | 'id'>;
    selectedPricingMerchandiseOptions: ReadonlyArray<{
      pricingTables: Array<
        Pick<PricingTable, 'creativeTypeId'> & {
          cells: Array<
            Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] }
          >;
        }
      >;
    }>;
    attribute: {
      aspectRatio?: Maybe<Pick<CreativeFormatAspectRatio, 'size'>>;
      playbackTime?: Maybe<Pick<CreativeFormatPlaybackTime, 'seconds'>>;
    };
  },
  expressDueDays: number,
  selectedPricingMerchandiseOptions?: ReadonlyArray<{
    pricingTables: Array<
      Pick<PricingTable, 'creativeTypeId'> & {
        cells: Array<
          Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] }
        >;
      }
    >;
  }>,
): number => {
  const selectedOptions = selectedPricingMerchandiseOptions
    ? selectedPricingMerchandiseOptions
    : creativeFormat.selectedPricingMerchandiseOptions || [];
  return selectedOptions.reduce((accum, pricingMerchandiseOption) => {
    const newPricingPoint = getPointFromCreativeFormat(pricingMerchandiseOption, creativeFormat, 'new');
    const expressPricingPoint = getPointFromCreativeFormat(pricingMerchandiseOption, creativeFormat, 'express');
    return accum + newPricingPoint.point + expressPricingPoint.point * (expressDueDays || 0);
  }, 0);
};

const getPointFromCreativeFormat = (
  option: {
    pricingTables: Array<
      Pick<PricingTable, 'creativeTypeId'> & {
        cells: Array<
          Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] }
        >;
      }
    >;
  },
  creativeFormat: Pick<CreativeFormat, 'resizeBaseId'> & {
    creativeType: Pick<CreativeFormatCreativeType, 'key' | 'id'>;
    attribute: {
      aspectRatio?: Maybe<Pick<CreativeFormatAspectRatio, 'size'>>;
      playbackTime?: Maybe<Pick<CreativeFormatPlaybackTime, 'seconds'>>;
    };
  },
  itemType: CreativeFormatItemType,
): Pick<ProductPricing, 'point'> => {
  const getPricingKeyFromCreativeFormat = (
    creativeFormat: Pick<CreativeFormat, 'resizeBaseId'>,
    itemType: CreativeFormatItemType,
  ) => {
    const actionType = creativeFormat.resizeBaseId ? 'resize' : 'create';
    if (itemType === 'express') {
      const camelizedActionType = actionType.slice(0, 1).toUpperCase() + actionType.slice(1);
      return `expressPerDayFor${camelizedActionType}` as ProductPricingKey;
    } else {
      return actionType;
    }
  };
  const getPointFromPricingCell = (
    cell: {
      prices: Array<Pick<ProductPricing, 'key' | 'point'>>;
    },
    creativeFormat: Pick<CreativeFormat, 'resizeBaseId'>,
    itemType: CreativeFormatItemType,
  ) => {
    const price = cell.prices.find((_price) => _price.key == getPricingKeyFromCreativeFormat(creativeFormat, itemType));
    return price!;
  };
  const getDisplaySizeKey = (size: DisplaySize): string => {
    return `${size.width}x${size.height}`;
  };
  const getPointFromPricingTable = (
    pricingTable: {
      cells: Array<
        Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] }
      >;
    },
    creativeFormat: Pick<CreativeFormat, 'resizeBaseId'> & {
      creativeType: Pick<CreativeFormatCreativeType, 'key'>;
      attribute: {
        aspectRatio?: Maybe<Pick<CreativeFormatAspectRatio, 'size'>>;
        playbackTime?: Maybe<Pick<CreativeFormatPlaybackTime, 'seconds'>>;
      };
    },
    itemType: CreativeFormatItemType,
  ): Pick<ProductPricing, 'point'> => {
    const supportingAspectRatioKey = getDisplaySizeKey(creativeFormat.attribute.aspectRatio!.size);
    const supportingPlaybackTimeLength = creativeFormat.attribute.playbackTime
      ? creativeFormat.attribute.playbackTime.seconds.toString()
      : null;

    let cells: ReadonlyArray<
        Pick<PricingTableCell, 'supportingAspectRatioKey' | 'supportingPlaybackTimeLength'> & {
          prices: Pick<ProductPricing, 'point' | 'key'>[];
        }
      >,
      cell:
        | (Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] })
        | undefined;

    cells = pricingTable.cells.filter((_cell) => {
      return _cell.supportingAspectRatioKey == supportingAspectRatioKey;
    });
    if (cells.length == 0) {
      cells = pricingTable.cells.filter((_cell) => {
        return _cell.supportingAspectRatioKey == 'all';
      });
    }
    if (creativeFormat.creativeType.key === 'static_image') {
      cell = cells[0];
    } else {
      cell = cells.find((_cell) => {
        return _cell.supportingPlaybackTimeLength == supportingPlaybackTimeLength;
      });
    }

    if (!cell) {
      throw new Error('this format`s point not found');
    }

    return getPointFromPricingCell(cell, creativeFormat, itemType);
  };

  const pricingTable = option.pricingTables.find(
    (_pricingTable) => _pricingTable.creativeTypeId == creativeFormat.creativeType.id,
  );
  return getPointFromPricingTable(pricingTable!, creativeFormat, itemType);
};

// 入力中の合計金額（saveAsDraftされていないもの
export const calculateInputtedTotalPoint = (
  creationSet: InputtedCreationSet,
  availableOptions: ReadonlyArray<Option>,
  selectedOptions: ReadonlyArray<string>,
  expressDueDays?: number,
) => {
  const optionPoint = calculateSelectedOptionTotalPoint(creationSet, availableOptions, selectedOptions);
  return (
    (creationSet.creativeFormats.reduce((accum, data) => {
      const expressPerDay = calculateExpressPerDay(data, expressDueDays);
      const point = calculatePoint(data);
      return accum + (point || 0) + expressPerDay;
    }, 0) +
      optionPoint) *
    creationSet.amount
  );
};

// 入力済みの合計金額（saveAsDraftされてDBに存在する値
// CreationSet.amountでクリエイター分の計算も含める
export function calculateTotalPoint(
  creationSet: Pick<CreationSet, 'amount'> & {
    creativeFormats: Array<
      Pick<CreativeFormat, 'resizeBaseId'> & {
        creativeType: {
          requiredPoints: Array<Pick<ProductPricing, 'key' | 'point'>>;
        };
        attribute: {
          aspectRatio?: Maybe<{
            requiredOption?: Maybe<
              Pick<ProductOption, 'amount'> & {
                points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
              }
            >;
          }>;
          playbackTime?: Maybe<{
            requiredOption?: Maybe<
              Pick<ProductOption, 'amount'> & {
                points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
              }
            >;
          }>;
        };
      }
    >;
  },
  expressDueDays?: number | null,
) {
  return (
    creationSet.creativeFormats.reduce((accum, data) => {
      const expressPerDay = calculateExpressPerDay(data, expressDueDays);
      const point = calculatePoint(data);
      return accum + (point || 0) + expressPerDay;
    }, 0) * creationSet.amount
  );
}

// CreationSet.amountを含めない
export function calculateSubTotalPoint(creationSet: CreationSet, expressDueDays?: number) {
  return creationSet.creativeFormats.reduce((accum, data) => {
    const expressPerDay = calculateExpressPerDay(data, expressDueDays);
    const point = calculatePoint(data);
    return accum + (point || 0) + expressPerDay;
  }, 0);
}

// CreationSet.amountを含めない
export const calculateInputtedSubTotalPoint = (
  creationSet: InputtedCreationSet,
  availableOptions: ReadonlyArray<Option>,
  selectedOptions: ReadonlyArray<string>,
  expressDueDays?: number,
) => {
  const optionPoint = calculateSelectedOptionTotalPoint(creationSet, availableOptions, selectedOptions);
  return (
    creationSet.creativeFormats.reduce((accum, data) => {
      const expressPerDay = calculateExpressPerDay(data, expressDueDays);
      const point = calculatePoint(data);
      return accum + (point || 0) + expressPerDay;
    }, 0) + optionPoint
  );
};

export type CreationSetForCalculateTotalPointForAdditionalPurchase = Pick<CreationSet, 'expressDueDays'> & {
  creativeFormats: Array<
    Pick<CreativeFormat, 'resizeBaseId'> & {
      creativeType: Pick<CreativeFormatCreativeType, 'key' | 'id'> & {
        requiredPoints: Array<Pick<ProductPricing, 'key' | 'point'>>;
      };
      selectedPricingMerchandiseOptions: ReadonlyArray<{
        pricingTables: Array<
          Pick<PricingTable, 'creativeTypeId'> & {
            cells: Array<
              Pick<PricingTableCell, 'supportingAspectRatioKey'> & { prices: Pick<ProductPricing, 'point' | 'key'>[] }
            >;
          }
        >;
      }>;
      attribute: {
        aspectRatio?: Maybe<
          Pick<CreativeFormatAspectRatio, 'size'> & {
            requiredOption?: Maybe<
              Pick<ProductOption, 'amount'> & {
                points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
              }
            >;
          }
        >;
        playbackTime?: Maybe<
          Pick<CreativeFormatPlaybackTime, 'seconds'> & {
            requiredOption?: Maybe<
              Pick<ProductOption, 'amount'> & {
                points: Array<Pick<ProductOptionPoint, 'key' | 'point'>>;
              }
            >;
          }
        >;
      };
    }
  >;
};

// CreationSet単位で金額を取得し、購入数分掛け算
export function calculateTotalPointForAdditionalPurchase(
  creationSet: CreationSetForCalculateTotalPointForAdditionalPurchase,
  purchaseAmount: number,
) {
  const expressDueDays = creationSet.expressDueDays;
  return (
    creationSet.creativeFormats.reduce((accum, data) => {
      const point = calculatePoint(data);
      const expressPoint = expressDueDays ? calculateExpressPerDay(data, expressDueDays) : 0;
      const merchandisePoint = calculateOptionMerchandisePoint(data, expressDueDays || 0);
      return accum + (point || 0) + expressPoint + merchandisePoint;
    }, 0) * purchaseAmount
  );
}
